/**
 * DOCTOR-HOME-STATE-MODEL-V1 -- THE ONE doctor visit-state machine (founder
 * Home-screen walk verdicts F1, 2026-08-14).
 *
 * ROOT CAUSE THIS MODULE RETIRES: the doctor surface carried FOUR independent
 * copies of the in-consult predicate (doctor-home-view myDayStats +
 * doctorQueueBand, ClinicDayBoard isInConsultation, doctor-context-view
 * drawerVisitChip), none arbitrating the chair, and the row chip read
 * appointments.status (desk plane) while the row door read case_sheets.status
 * (doctor plane) -- so ONE visit could render "Visit complete" beside
 * "Resume consult", and every open DRAFT counted as "In consult" ("4-5 In
 * consult" while one patient sat in the chair). This module is now the ONE
 * derivation owner; the doctor home, the doctor context drawer, and the
 * front-desk board's visit ops predicate all CONSUME it (the board's layout
 * is untouched -- it consumes, it does not re-derive).
 *
 * THE STATES (founder F1 vocabulary, one per row per day):
 *   in_chair    -- EXACTLY ONE row at most: the consult the doctor is
 *                  actually running (present + consult open, arbitrated
 *                  cross-row; latest consult_started wins).
 *   draft_open  -- a consult record is open but this is NOT the chair:
 *                  owed paperwork. The row says WHAT is owed (the case
 *                  sheet), and its door stays "Resume consult" -- which
 *                  NEVER renders as "In consult" anywhere.
 *   waiting     -- physically present (arrived / checked_in), nothing open.
 *   expected    -- booked / confirmed / rescheduled / waitlist: not here yet.
 *   finalized   -- THE doctor close (doctorConsultationClosed truth,
 *                  FULFILLED-DERIVATION-CORRECTIVE-V1 unchanged): tested
 *                  FIRST so a stale consult_started event can never re-open
 *                  a closed visit, and a completed row can never show Resume.
 *   cancelled / no_show -- the visit ended without a consultation
 *                  (terminal-negative; a stray draft on such a row is
 *                  contradictory evidence and never renders as in-consult).
 *
 * PLANE LAW: this machine derives the DOCTOR plane. The desk plane
 * (appointments.status vocabulary -- "Visit complete" on a desk fulfil) stays
 * the desk's own truth on desk surfaces; the doctor surface renders ONLY this
 * machine's states, so the two planes can no longer contradict on one row.
 * Display-only: nothing here writes a status (LES-009 TRUTH-DISPLAY).
 *
 * Pure + structural: callers pass any row shape carrying the fields below
 * (TodayAppointment satisfies it); the module imports nothing from feature
 * directories so every surface may consume it without cycles.
 */

export type DoctorVisitState =
  | 'in_chair'
  | 'draft_open'
  | 'case_sheet_owed'
  | 'waiting'
  | 'expected'
  | 'finalized'
  | 'cancelled'
  | 'no_show';

/** The row facts the machine reads -- all already on the board wire. */
export interface DoctorVisitStateRow {
  readonly id: number;
  /** appointments.status (stored, desk-owned). */
  readonly status: string;
  /** Board-wire case-sheet projection: 'none' | 'draft' | 'finalized';
   *  absent (undefined) on a non-board read = unknown, never asserted. */
  readonly visitStatus?: string | null;
  /** The consult_started EVENT (R-07); absent = unknown. */
  readonly consultStarted?: boolean | null;
  /** The consult_started event's recorded stamp; null/absent = no stamp. */
  readonly consultStartedAt?: string | null;
  /** The patient_arrived event's recorded stamp; null/absent = none. */
  readonly arrivedAt?: string | null;
  /** The slot time (tiebreak only). */
  readonly appointmentTime?: string | null;
}

/** The stored statuses that mean the person is PHYSICALLY IN THE CLINIC. */
const PRESENT_STATUSES: readonly string[] = ['arrived', 'checked_in'];

/** Terminal-negative stored statuses: the visit ended with no consultation
 *  left to close (DONE_TODAY_STATUSES truth, unchanged by this package). */
const ENDED_STATUSES: readonly string[] = ['cancelled', 'no_show'];

/**
 * THE ONE per-row consult-open predicate: a consult is OPEN on this visit --
 * a draft case sheet exists (an open record is open wherever its person is),
 * OR the consult_started event fired while the person is still PRESENT (the
 * R-07 event-first law + the D4 arrived-is-startable law: after a start on
 * an ARRIVED row the stored status stays 'arrived' until the desk checks
 * in, and the consult is live all the same). A started event on a
 * departed, sheet-less row (desk-closed / pre-arrival) is STALE evidence,
 * not an open consult -- there is no record to resume, and the row's door
 * correctly reads Start. Never open on a finalized or ended visit (the
 * finalized-first guard is BUILT IN; previously only the board's copy
 * carried it). This is the predicate the front-desk board's "In
 * consultation" ops chip and the doctor context drawer consume.
 */
export function doctorConsultOpen(row: Pick<DoctorVisitStateRow, 'status'> & Partial<DoctorVisitStateRow>): boolean {
  if (row.visitStatus === 'finalized') return false;
  if (ENDED_STATUSES.includes(row.status)) return false;
  if (row.visitStatus === 'draft') return true;
  return row.consultStarted === true && PRESENT_STATUSES.includes(row.status);
}

/** Epoch ms for an ISO stamp, or null (absent/unparseable). */
function timeKey(value: string | null | undefined): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * THE CHAIR ARBITRATION (F1: "In chair (exactly one)"): among rows whose
 * consult is OPEN and whose person is PRESENT, exactly one is the chair.
 * Ranking, highest first (total and deterministic, never input-order
 * dependent):
 *   1. the consult_started EVENT beats a draft-only signal (R-07
 *      event-first: the doctor's Start/Resume CLICK is the strong signal;
 *      the autosaved draft is the fallback);
 *   2. the LATEST consult_started stamp (the most recently started consult
 *      is the live one); a stamp-less event ranks below any stamped one
 *      (an unknown start is an old start, never the live one);
 *   3. the latest arrival;
 *   4. the highest id.
 * Returns the chair row id, or null (nobody is being consulted right now).
 */
export function doctorChairId(
  rows: readonly DoctorVisitStateRow[],
): number | null {
  let chair: DoctorVisitStateRow | null = null;
  for (const row of rows) {
    if (!doctorConsultOpen(row)) continue;
    if (!PRESENT_STATUSES.includes(row.status)) continue;
    if (chair === null) {
      chair = row;
      continue;
    }
    const rowEvent = row.consultStarted === true;
    const chairEvent = chair.consultStarted === true;
    if (rowEvent !== chairEvent) {
      if (rowEvent) chair = row;
      continue;
    }
    const a = timeKey(row.consultStartedAt);
    const b = timeKey(chair.consultStartedAt);
    if (a !== b) {
      if (b === null || (a !== null && a > b)) chair = row;
      continue;
    }
    const arrA = timeKey(row.arrivedAt);
    const arrB = timeKey(chair.arrivedAt);
    if (arrA !== arrB) {
      if (arrB === null || (arrA !== null && arrA > arrB)) chair = row;
      continue;
    }
    if (row.id > chair.id) chair = row;
  }
  return chair === null ? null : chair.id;
}

/**
 * Derive ONE row's state given the day's arbitrated chair id (first-match,
 * total). CLOSED FIRST: the finalized test outranks every in-progress
 * signal, so "Resume consult" on a completed row is impossible by
 * construction -- the door derivation (consultDoorForRow) reads the same
 * finalized-first order.
 */
export function doctorVisitStateForRow(
  row: DoctorVisitStateRow,
  chairId: number | null,
): DoctorVisitState {
  if (row.visitStatus === 'finalized') return 'finalized';
  if (row.status === 'cancelled') return 'cancelled';
  if (row.status === 'no_show') return 'no_show';
  if (doctorConsultOpen(row)) {
    return row.id === chairId ? 'in_chair' : 'draft_open';
  }
  if (row.status === 'fulfilled' || row.status === 'completed') return 'case_sheet_owed';
  if (PRESENT_STATUSES.includes(row.status)) return 'waiting';
  return 'expected';
}

/** F2 header counts -- the five founder-ruled numbers, one derivation. */
export interface DoctorDayCounts {
  /** Present (arrived / checked_in), not the chair, not closed -- who is
   *  physically waiting, whether or not paperwork is open on them. */
  readonly waiting: number;
  /** The chair: 0 or 1, NEVER the draft count (founder F1 root cause). */
  readonly inConsult: number;
  /** Finalized consultations (doctorConsultationClosed truth, unchanged). */
  readonly completed: number;
  /** Not here yet: booked / confirmed / rescheduled / waitlist. */
  readonly expected: number;
  /** F2 NEW: cancelled visits, from the existing stored status. */
  readonly cancelled: number;
}

export interface DoctorDayModel {
  readonly chairId: number | null;
  /** Every row's derived state, keyed by appointment id. */
  readonly stateById: ReadonlyMap<number, DoctorVisitState>;
  readonly counts: DoctorDayCounts;
}

/**
 * THE DAY MODEL: one pass, one truth -- header counts, the chair, and every
 * row's state from the SAME derivation (the header can no longer disagree
 * with the rows it summarizes). A desk-closed row awaiting its consultation
 * (fulfilled/completed + unfinalized) counts in NO tile -- the recorded
 * FULFILLED-DERIVATION-CORRECTIVE-V1 consequence, unchanged -- while its row
 * now states the honest 'draft_open' owed-paperwork truth. A no_show counts
 * in no tile either (the Done bar names it).
 */
export function deriveDoctorDayModel(
  rows: readonly DoctorVisitStateRow[],
): DoctorDayModel {
  const chairId = doctorChairId(rows);
  const stateById = new Map<number, DoctorVisitState>();
  let waiting = 0;
  let inConsult = 0;
  let completed = 0;
  let expected = 0;
  let cancelled = 0;
  for (const row of rows) {
    const state = doctorVisitStateForRow(row, chairId);
    stateById.set(row.id, state);
    switch (state) {
      case 'finalized':
        completed += 1;
        break;
      case 'in_chair':
        inConsult += 1;
        break;
      case 'cancelled':
        cancelled += 1;
        break;
      case 'expected':
        expected += 1;
        break;
      case 'waiting':
        waiting += 1;
        break;
      case 'draft_open':
        // Owed paperwork. A PRESENT person with an open draft is still
        // physically waiting for the doctor, so they count in Waiting; a
        // desk-closed or departed draft counts in no tile (recorded above).
        if (PRESENT_STATUSES.includes(row.status)) waiting += 1;
        break;
      case 'case_sheet_owed':
      case 'no_show':
        break;
    }
  }
  return { chairId, stateById, counts: { waiting, inConsult, completed, expected, cancelled } };
}

/**
 * THE QUEUE BAND (W-5 corrective, founder tower walk 2026-08-14 -- band
 * authority moved INTO the owner): the visible group a row files under,
 * derived from the SAME truth as its state so grouping can never contradict
 * the chip. Bands, in actionability order:
 *   0 in_consult  -- the chair (exactly one).
 *   1 present     -- physically in the clinic (waiting, or a PRESENT open
 *                    draft), ordered by arrival by the consumer.
 *   2 draft_open  -- a desk-closed/departed OPEN DRAFT: owed paperwork with
 *                    its OWN group. NEVER filed under Expected beside
 *                    not-arrived rows (the walked W-5 edge: a
 *                    completed-visit-with-open-draft grouped with Booked).
 *   3 expected    -- not here yet (booked / confirmed / rescheduled /
 *                    waitlist).
 * Done states (finalized / cancelled / no_show) never reach banding -- the
 * done split happens first (doctorRowIsDoneToday).
 */
export function doctorQueueBandForRow(
  row: DoctorVisitStateRow,
  chairId: number | null,
): 0 | 1 | 2 | 3 {
  if (row.id === chairId) return 0;
  if (PRESENT_STATUSES.includes(row.status)) return 1;
  if (doctorConsultOpen(row) || row.status === 'fulfilled' || row.status === 'completed') return 2;
  return 3;
}

/** The owed-paperwork statement (F1 "says what is owed" / F6c "WHAT is
 *  owed") -- ONE vocabulary for the day row's draft chip and the owed-work
 *  inbox row's chip. */
export const DOCTOR_CASE_SHEET_OWED_LABEL = 'Case sheet owed';

export interface DoctorVisitStateChip {
  readonly label: string;
  readonly tone: 'brand' | 'pending' | 'positive' | 'info' | 'neutral' | 'danger';
}

/**
 * The row chip for a derived state -- THE one doctor-plane vocabulary. The
 * expected band keeps the stored-status word (Booked / Confirmed / ... --
 * the appointment canon labels, resolved by the caller's existing
 * dayStatusChip) because "not here yet" is the one band where the desk word
 * IS the doctor's truth; every other state renders this machine's word, so
 * the desk chip and the doctor door can never contradict on one row.
 */
export function doctorVisitStateChip(
  state: DoctorVisitState,
): DoctorVisitStateChip | null {
  switch (state) {
    case 'in_chair':
      return { label: 'In consult', tone: 'brand' };
    case 'draft_open':
      return { label: 'Draft open', tone: 'pending' };
    case 'case_sheet_owed':
      return { label: DOCTOR_CASE_SHEET_OWED_LABEL, tone: 'pending' };
    case 'waiting':
      return { label: 'Waiting', tone: 'brand' };
    case 'finalized':
      return { label: 'Visit finalized', tone: 'positive' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'neutral' };
    case 'no_show':
      return { label: 'No show', tone: 'neutral' };
    case 'expected':
      // Caller renders the stored-status chip (the appointment canon word).
      return null;
  }
}
